import { Table } from '@cowbell/ui/core';
import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { SourcesCitation } from '../../Sources/SourcesCitation';
import markdownStyles from '../ChatMarkdown.module.css';
import bubbleStyles from '../MessageBubble.module.css';

/* GFM markdown tables → Mantine Table so they follow the theme. Quiet by
 * design: one hairline frame, row rules only, no zebra or column borders,
 * and tabular figures so numbers line up. react-markdown maps each table
 * tag to these overrides; remark-gfm encodes column alignment as an inline
 * `text-align` style on `th`/`td`, so those two forward `style`. */
export function AssistantMDTable({
  children,
}: Readonly<{ children?: ReactNode }>) {
  return (
    <div className={markdownStyles.tableWrap} data-print-table-wrapper>
      <Table
        highlightOnHover
        verticalSpacing="0.5rem"
        horizontalSpacing="0.75rem"
      >
        {children}
      </Table>
    </div>
  );
}

/** Bridges `<span data-citation="N" />` HTML placeholders emitted by
 *  `remarkCitations` to the React `SourcesCitation` chip. The data attr
 *  arrives camelCased through the rehype → React prop pipeline; older
 *  rehype versions may still pass the dash form, so accept both. */
type CitationSpanProps = ComponentPropsWithoutRef<'span'> & {
  dataCitation?: string;
  'data-citation'?: string;
};

export function MarkdownSpan(props: CitationSpanProps) {
  const { dataCitation, 'data-citation': dataCitationDash, ...rest } = props;
  const raw = dataCitation ?? dataCitationDash;
  if (raw !== undefined) {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isFinite(parsed)) {
      return <SourcesCitation citationIndex={parsed} />;
    }
  }
  return <span {...rest} />;
}

export function MarkdownThead({
  children,
}: Readonly<{ children?: ReactNode }>) {
  return (
    <Table.Thead className={bubbleStyles.tableHeader}>{children}</Table.Thead>
  );
}

export function MarkdownTbody({
  children,
}: Readonly<{ children?: ReactNode }>) {
  return <Table.Tbody>{children}</Table.Tbody>;
}

export function MarkdownTr({ children }: Readonly<{ children?: ReactNode }>) {
  return <Table.Tr>{children}</Table.Tr>;
}

export function MarkdownTh({
  children,
  style,
}: Readonly<{ children?: ReactNode; style?: CSSProperties }>) {
  return <Table.Th style={style}>{children}</Table.Th>;
}

export function MarkdownTd({
  children,
  style,
}: Readonly<{ children?: ReactNode; style?: CSSProperties }>) {
  return <Table.Td style={style}>{children}</Table.Td>;
}
